import React from "react";
import { MediaAccordion, type AccordionItemContent } from "ui/accordion";
import { LottieWithStaticSafariImage } from "ui/lottie";
import { Link } from "ui/link";
import firstAccordionImage from "../../../../../public/assets/platform/match-booster/accordion-1.png";
import secondAccordionImage from "../../../../../public/assets/platform/match-booster/accordion-2.png";
import thirdAccordionImage from "../../../../../public/assets/platform/match-booster/accordion-3.png";
import fourthAccordionImage from "../../../../../public/assets/platform/match-booster/accordion-4.png";
import fifthAccordionImage from "../../../../../public/assets/platform/match-booster/accordion-5.png";
import firstAccordionAnimation from "./accordion-1.json";
import secondAccordionAnimation from "./accordion-2.json";
import thirdAccordionAnimation from "./accordion-3.json";
import fourthAccordionAnimation from "./accordion-4.json";
import fifthAccordionAnimation from "./accordion-5.json";

const lottieRatio = 872 / 648;
const lottieViewportAmount = 0.7;

const accordionItems: AccordionItemContent[] = [
  {
    title: "User & Device Matching",
    content:
      "Increase audience reach by enhancing every sync with third-party identifiers that help media channels match more of your customers to addressable users.",
    media: (
      <LottieWithStaticSafariImage
        ratio={lottieRatio}
        viewportAmount={lottieViewportAmount}
        animationData={firstAccordionAnimation}
        loop={false}
        image={firstAccordionImage}
        imagePlaceholder="blur"
        alt="Illustration of Elise Meyers, Director of Operation's profile with matched profiles for Pinterest, TikTok, Facebook, Instagram, Google, and Snapchat."
        placeholderBgColor="new.violet.150"
      />
    ),
  },
  {
    title: "Event Matching",
    content: (
      <>
        Convert more customers at a lower cost by improving the volume and event quality of your{" "}
        <Link
          href="/blog/announcing-match-booster-conversion-api"
          variant="inlineDark"
          target="_blank"
          _hover={{ color: "new.green.300" }}
        >
          conversion events
        </Link>{" "}
        — the data ad platforms rely on to optimize targeting, bidding, and budget allocation.
      </>
    ),
    media: (
      <LottieWithStaticSafariImage
        ratio={lottieRatio}
        animationData={secondAccordionAnimation}
        image={secondAccordionImage}
        imagePlaceholder="blur"
        alt="Abstract illustration of a data warehouse with logos of various ad platforms like Facebook, Snapchat, Google, and TikTok all with increased match rates."
        placeholderBgColor="new.green.150"
      />
    ),
  },
  {
    title: "B2B Matching",
    content:
      "Reach more B2B decision makers across paid search, social, display, and connected TV by enhancing your business contacts (e.g. business emails) with consumer identifiers (e.g. personal email, phone, MAIDs, etc).",
    media: (
      <LottieWithStaticSafariImage
        ratio={lottieRatio}
        animationData={thirdAccordionAnimation}
        image={thirdAccordionImage}
        imagePlaceholder="blur"
        alt="Illustration of Elise Meyers, Director of Operation's user profile with identifiers for her employer, city and state location, email, and phone number."
        placeholderBgColor="new.blue.150"
      />
    ),
  },
  {
    title: "Household Matching",
    content:
      "Broaden your targeting or suppression list from known and anonymous profiles to entire households, including family members, roommates, and shared devices.",
    media: (
      <LottieWithStaticSafariImage
        ratio={lottieRatio}
        animationData={fourthAccordionAnimation}
        loop={false}
        image={fourthAccordionImage}
        imagePlaceholder="blur"
        alt="Abstract illustration depicting a tree graph. There is a user's home at the base, with first-level branches extending to two different user profiles and a smart TV, all with unique identifiers."
        placeholderBgColor="new.yellow.150"
      />
    ),
  },
  {
    title: "Anonymous Visitor Matching",
    content:
      "Reach high-intent anonymous users by matching IP addresses to known profiles and devices. No 3rd-party cookies required.",
    media: (
      <LottieWithStaticSafariImage
        ratio={lottieRatio}
        animationData={fifthAccordionAnimation}
        initialSegment={[40, 300]}
        loop={false}
        image={fifthAccordionImage}
        imagePlaceholder="blur"
        alt="Illustration of an anonymous user profile that has been enriched into a known user profile for Robert S., with identifiers for his employer, address, email, and phone number."
        placeholderBgColor="new.green.150"
      />
    ),
  },
];

export function FeatureAccordion() {
  return (
    <MediaAccordion
      prerenderMedia={false}
      variant="dynamic"
      direction="right"
      theme="dark"
      size="lg"
      mt={{ base: "big", md: "huge" }}
      items={accordionItems}
    />
  );
}
